/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Repeat2, Phone, Mail, Clock, CheckCircle, PhoneCall, Archive, CalendarPlus, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  da_gestire: { label: 'Da gestire', cls: 'bg-amber-100 text-amber-800' },
  contattato: { label: 'Contattato', cls: 'bg-blue-100 text-blue-800' },
  ricollocato: { label: 'Ricollocato', cls: 'bg-emerald-100 text-emerald-800' },
  chiuso: { label: 'Chiuso', cls: 'bg-gray-100 text-gray-600' },
};

function fmtDate(d) {
  try { return format(parseISO(d), 'd MMMM yyyy', { locale: it }); } catch { return d; }
}

export default function Ricollocazioni() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('da_gestire');

  // Rebook dialog state
  const [rebookTask, setRebookTask] = useState(null);
  const [availDates, setAvailDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [selDate, setSelDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selSlot, setSelSlot] = useState(null);
  const [booking, setBooking] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rebooking-tasks`);
      setTasks(res.data);
    } catch (err) {
      logger.error('Rebooking tasks error:', err);
      toast.error('Errore nel caricamento delle segnalazioni');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const setStatus = async (task, status, newApptId = null) => {
    try {
      const body = { status };
      if (newApptId) body.new_appointment_id = newApptId;
      await axios.put(`${API}/rebooking-tasks/${task.id}/status`, body);
      toast.success(`Segnalazione aggiornata: ${STATUS_META[status].label}`);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const openRebook = async (task) => {
    setRebookTask(task);
    setSelDate('');
    setSlots([]);
    setSelSlot(null);
    setDatesLoading(true);
    try {
      const res = await axios.get(`${API}/availability/dates`, { params: { doctor_id: task.doctor_id } });
      setAvailDates(res.data.dates || []);
    } catch {
      setAvailDates([]);
    } finally {
      setDatesLoading(false);
    }
  };

  useEffect(() => {
    if (!rebookTask || !selDate) { setSlots([]); setSelSlot(null); return; }
    setSlotsLoading(true);
    setSelSlot(null);
    axios.get(`${API}/availability`, { params: { doctor_id: rebookTask.doctor_id, data: selDate } })
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [rebookTask, selDate]);

  const handleRebook = async () => {
    if (!rebookTask || !selDate || !selSlot) return;
    setBooking(true);
    try {
      const res = await axios.post(`${API}/appointments`, {
        doctor_id: rebookTask.doctor_id,
        service_id: rebookTask.service_id,
        data: selDate,
        availability_id: selSlot.availability_id,
        patient_id: rebookTask.patient_id,
        note_paziente: 'Ricollocazione a seguito di annullamento del medico',
      });
      await axios.put(`${API}/rebooking-tasks/${rebookTask.id}/status`, {
        status: 'ricollocato',
        new_appointment_id: res.data.id,
      });
      toast.success('Paziente ricollocato con successo');
      setRebookTask(null);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella ricollocazione');
    } finally {
      setBooking(false);
    }
  };

  const filtered = filter === 'tutte' ? tasks : tasks.filter(t => t.status === filter);
  const counts = tasks.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});

  return (
    <div>
      <div className="mb-6">
        <PageTitle title="Ricollocazioni" info="Pazienti da avvertire e ricollocare dopo l'annullamento di un appuntamento da parte del medico." />
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList data-testid="rebooking-filter-tabs">
          <TabsTrigger value="da_gestire">Da gestire {counts.da_gestire ? `(${counts.da_gestire})` : ''}</TabsTrigger>
          <TabsTrigger value="contattato">Contattati {counts.contattato ? `(${counts.contattato})` : ''}</TabsTrigger>
          <TabsTrigger value="ricollocato">Ricollocati {counts.ricollocato ? `(${counts.ricollocato})` : ''}</TabsTrigger>
          <TabsTrigger value="chiuso">Chiusi</TabsTrigger>
          <TabsTrigger value="tutte">Tutte</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Repeat2 size={40} className="mx-auto text-[#238A92] mb-3" />
            <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Nessuna segnalazione</p>
            <p className="text-sm text-muted-foreground mt-1">
              {filter === 'da_gestire' ? 'Non ci sono pazienti da ricollocare al momento.' : 'Nessuna segnalazione in questo stato.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table data-testid="rebooking-tasks-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Paziente</TableHead>
                  <TableHead>Contatti</TableHead>
                  <TableHead>Visita annullata</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(t => (
                  <TableRow key={t.id} data-testid={`rebooking-task-${t.id}`}>
                    <TableCell>
                      <p className="font-medium text-sm">{t.patient_nome} {t.patient_cognome}</p>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5 text-xs text-muted-foreground">
                        {t.patient_email && <p className="flex items-center gap-1"><Mail size={11} /> {t.patient_email}</p>}
                        {t.patient_telefono && <p className="flex items-center gap-1"><Phone size={11} /> {t.patient_telefono}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{t.service_nome}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock size={11} /> {fmtDate(t.data)} ore {t.ora} — Dott. {t.doctor_cognome}
                      </p>
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <p className="text-xs text-muted-foreground">{t.reason}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_META[t.status]?.cls || ''} text-xs`}>{STATUS_META[t.status]?.label || t.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5 flex-wrap">
                        {(t.status === 'da_gestire') && (
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setStatus(t, 'contattato')} data-testid={`task-contacted-${t.id}`}>
                            <PhoneCall size={13} className="mr-1" /> Contattato
                          </Button>
                        )}
                        {(t.status === 'da_gestire' || t.status === 'contattato') && (
                          <>
                            <Button size="sm" className="h-8 text-xs bg-[#238A92] hover:bg-[#0F5E66] text-white" onClick={() => openRebook(t)} data-testid={`task-rebook-${t.id}`}>
                              <CalendarPlus size={13} className="mr-1" /> Riprenota
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 text-xs text-muted-foreground" onClick={() => setStatus(t, 'chiuso')} data-testid={`task-close-${t.id}`}>
                              <Archive size={13} className="mr-1" /> Chiudi
                            </Button>
                          </>
                        )}
                        {t.status === 'ricollocato' && (
                          <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle size={13} /> Nuovo appuntamento creato</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Rebook dialog */}
      <Dialog open={!!rebookTask} onOpenChange={(o) => !o && setRebookTask(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Riprenota per {rebookTask?.patient_nome} {rebookTask?.patient_cognome}</DialogTitle>
          </DialogHeader>
          {rebookTask && (
            <div className="space-y-4">
              <div className="bg-[hsl(var(--secondary))] rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-muted-foreground">Servizio:</span> <span className="font-medium">{rebookTask.service_nome}</span></p>
                <p><span className="text-muted-foreground">Medico:</span> <span className="font-medium">Dott. {rebookTask.doctor_nome} {rebookTask.doctor_cognome}</span></p>
              </div>

              <div>
                <p className="text-sm font-medium mb-1.5">Nuova data</p>
                {datesLoading ? (
                  <Skeleton className="h-10 rounded-lg" />
                ) : availDates.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Il medico non ha altre disponibilità. Aggiungile da “Disponibilità” o contatta il paziente per altre soluzioni.</p>
                ) : (
                  <Select value={selDate} onValueChange={setSelDate}>
                    <SelectTrigger data-testid="rebook-date-select">
                      <SelectValue placeholder="Seleziona una data disponibile" />
                    </SelectTrigger>
                    <SelectContent>
                      {availDates.map(d => (
                        <SelectItem key={d} value={d} className="capitalize">{fmtDate(d)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {selDate && (
                <div>
                  <p className="text-sm font-medium mb-1.5">Orario</p>
                  {slotsLoading ? (
                    <div className="grid grid-cols-4 gap-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-9 rounded-lg" />)}</div>
                  ) : slots.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nessuno slot libero in questa data.</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2 max-h-44 overflow-y-auto">
                      {slots.map(s => (
                        <button
                          key={s.availability_id}
                          type="button"
                          onClick={() => setSelSlot(s)}
                          className={`py-2 px-2 rounded-lg border text-sm font-medium transition-colors duration-150 ${
                            selSlot?.availability_id === s.availability_id
                              ? 'bg-[#238A92] text-white border-[#238A92]'
                              : 'border-[hsl(var(--border))] hover:border-[#238A92]'
                          }`}
                          data-testid={`rebook-slot-${s.ora}`}
                        >
                          {s.ora}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRebookTask(null)}>Annulla</Button>
            <Button
              disabled={!selDate || !selSlot || booking}
              onClick={handleRebook}
              className="bg-[#238A92] hover:bg-[#0F5E66] text-white"
              data-testid="rebook-confirm-button"
            >
              {booking ? <Loader2 size={15} className="mr-1 animate-spin" /> : <CheckCircle size={15} className="mr-1" />}
              Conferma Ricollocazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
